import { BatteryFullIcon, BatteryLowIcon, BatteryMediumIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

function toneOf(socPercent: number): 'green' | 'yellow' | 'red' {
  if (socPercent >= 50) return 'green';
  if (socPercent >= 20) return 'yellow';
  return 'red';
}

function BatteryIcon({ socPercent }: { socPercent: number | null }) {
  if (socPercent == null) return <BatteryMediumIcon className="size-11 shrink-0" aria-hidden />;
  if (socPercent >= 80) return <BatteryFullIcon className="size-11 shrink-0" aria-hidden />;
  if (socPercent >= 40) return <BatteryMediumIcon className="size-11 shrink-0" aria-hidden />;
  return <BatteryLowIcon className="size-11 shrink-0" aria-hidden />;
}

/** Battery percent for an electric or hybrid vehicle. `null` until a charge API exists. */
export function FleetChargeLevel({
  socPercent,
  presentation = 'pill',
  plate,
}: {
  socPercent: number | null;
  presentation?: 'pill' | 'tile';
  plate?: string;
}) {
  if (presentation === 'tile') {
    const tone = socPercent == null ? 'muted' : toneOf(socPercent);
    return (
      <div
        className={cn(
          'flex items-center gap-3 rounded-lg border px-3 py-3',
          tone === 'green' &&
            'border-green-200 bg-green-50 text-green-950 dark:border-green-900 dark:bg-green-950 dark:text-green-50',
          tone === 'yellow' &&
            'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-50',
          tone === 'red' &&
            'border-red-200 bg-red-50 text-red-950 dark:border-red-900 dark:bg-red-950 dark:text-red-50',
          tone === 'muted' && 'border-border bg-muted/40 text-foreground',
        )}
      >
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium tracking-wide uppercase">Ladestand</p>
          <p
            data-tone={socPercent == null ? undefined : tone}
            className="mt-1 text-2xl font-semibold tabular-nums"
          >
            {socPercent == null
              ? 'Keine Daten'
              : `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(socPercent)} %`}
          </p>
          <p className="mt-1 text-sm">{plate ?? 'Fahrzeug'}</p>
          {socPercent != null ? (
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-black/10 dark:bg-white/15">
              <div className="h-full rounded-full bg-current" style={{ width: `${socPercent}%` }} />
            </div>
          ) : null}
        </div>
        <BatteryIcon socPercent={socPercent} />
      </div>
    );
  }

  if (socPercent == null) {
    return (
      <span className="rounded-full bg-muted px-2 py-0.5 text-sm font-medium text-muted-foreground">
        Keine Daten
      </span>
    );
  }
  const tone = toneOf(socPercent);
  return (
    <span
      data-tone={tone}
      className={cn(
        'rounded-full px-2 py-0.5 text-sm font-semibold',
        tone === 'green' && 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200',
        tone === 'yellow' && 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
        tone === 'red' && 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
      )}
    >
      {new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(socPercent)} %
    </span>
  );
}
